import requests from './httpService'

export interface ApproveAndInitiatePayload {
  transactionReferences: string[]
  reason?: string
}

export interface ValidateBankAccountPayload {
  accountNumber: string
  bankCode?: string
}

export interface BankOption {
  bank_code: string
  bank_name: string
}

export interface UpdateFailedRefundPayload {
  bankCode?: string
  debitAccountNumber?: string
  debitAccountName?: string
  beneficiaryAccountName?: string
  beneficiaryBankName?: string
  beneficiaryAccountNumber?: string
  phoneNumber?: string
  email?: string
  reason?: string
}

/**
 * Calls the refund microservice THROUGH this backend's proxy routes (auth/proxy/refund-*),
 * not directly - the backend owns logging into the refund service and forwarding the request,
 * so this app only ever needs the normal backend session (see httpService.tsx), never the refund
 * service's own credentials. The proxy forwards the body as-is, so the snake_case shapes below
 * still match what the refund service itself expects.
 */
const RefundServiceApi = {
  /**
   * Reference list of beneficiary banks (not a refund-service proxy - this is plain reference
   * data served directly by this backend). BK ("40") is the only one BK's OpenAPI can actually
   * validate; the caller uses bank_code to decide whether to require verifyBankAccount first.
   */
  async getBankList (): Promise<BankOption[]> {
    const response = await requests.get('/system-settings/banks')
    return (response.data ?? []) as BankOption[]
  },

  async approveAndInitiateRefunds (payload: ApproveAndInitiatePayload): Promise<any> {
    const response = await requests.post('/auth/proxy/refund-approve-and-initiate', {
      transaction_references: payload.transactionReferences,
      reason: payload.reason
    })
    return response.data
  },

  async approveAndInitiateBailiffRefunds (payload: ApproveAndInitiatePayload): Promise<any> {
    const response = await requests.post('/auth/proxy/refund-bailiff-approve-and-initiate', {
      transaction_references: payload.transactionReferences,
      reason: payload.reason
    })
    return response.data
  },

  async validateBankAccount (payload: ValidateBankAccountPayload): Promise<any> {
    const response = await requests.post('/auth/proxy/refund-validate-bank-account', {
      account_number: payload.accountNumber,
      bank_code: payload.bankCode
    })
    return response.data
  },

  /** Edits a bidder refund currently in a failed state and resubmits it as PENDING. */
  async updateFailedRefund (transactionReference: string, payload: UpdateFailedRefundPayload): Promise<any> {
    const response = await requests.patch(
      `/auth/proxy/refund-update-failed/${encodeURIComponent(transactionReference)}`,
      {
        bank_code: payload.bankCode,
        debit_account_number: payload.debitAccountNumber,
        debit_account_name: payload.debitAccountName,
        beneficiary_account_name: payload.beneficiaryAccountName,
        beneficiary_bank_name: payload.beneficiaryBankName,
        beneficiary_account_number: payload.beneficiaryAccountNumber,
        phone_number: payload.phoneNumber,
        email: payload.email,
        reason: payload.reason
      }
    )
    return response.data
  },

  /** Bailiff-refund counterpart of {@link updateFailedRefund}. */
  async updateFailedBailiffRefund (transactionReference: string, payload: UpdateFailedRefundPayload): Promise<any> {
    const response = await requests.patch(
      `/auth/proxy/refund-bailiff-update-failed/${encodeURIComponent(transactionReference)}`,
      {
        bank_code: payload.bankCode,
        debit_account_number: payload.debitAccountNumber,
        debit_account_name: payload.debitAccountName,
        beneficiary_account_name: payload.beneficiaryAccountName,
        beneficiary_bank_name: payload.beneficiaryBankName,
        beneficiary_account_number: payload.beneficiaryAccountNumber,
        reason: payload.reason
      }
    )
    return response.data
  }
}

export default RefundServiceApi
